/**
 * 认证状态持久化工具
 * 
 * 自托管认证以 token + 本地备份会话的方式工作。
 * 在浏览器隐私模式或旧缓存残留场景下，这里提供额外的 localStorage 兜底。
 */

import { AuthUser } from '../types';

const AUTH_STORAGE_KEY = 'foodbook:auth:session';
const AUTH_STATE_KEY = 'foodbook:auth:state';

/**
 * 保存认证状态到 localStorage
 */
export function saveAuthState(user: AuthUser | null): void {
  try {
    if (user) {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
      localStorage.setItem(AUTH_STATE_KEY, JSON.stringify({
        isAuthenticated: true,
        timestamp: new Date().toISOString(),
      }));
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      localStorage.removeItem(AUTH_STATE_KEY);
    }
  } catch (error) {
    console.warn('[AuthPersistence] 保存认证状态失败:', error);
  }
}

/**
 * 从 localStorage 加载认证状态
 */
export function loadAuthState(): AuthUser | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const user = JSON.parse(stored) as AuthUser;
      console.log('[AuthPersistence] 从 localStorage 恢复认证状态:', user.email || user.id);
      return user;
    }
  } catch (error) {
    console.warn('[AuthPersistence] 加载认证状态失败:', error);
  }
  return null;
}

/**
 * 检查是否有保存的认证状态
 */
export function hasStoredAuthState(): boolean {
  try {
    const state = localStorage.getItem(AUTH_STATE_KEY);
    if (state) {
      const parsed = JSON.parse(state);
      // 检查状态是否过期（7天）
      const timestamp = new Date(parsed.timestamp).getTime();
      const now = Date.now();
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      
      if (now - timestamp < sevenDays) {
        return true;
      } else {
        // 过期，清除状态
        clearAuthState();
      }
    }
  } catch (error) {
    console.warn('[AuthPersistence] 检查认证状态失败:', error);
  }
  return false;
}

/**
 * 清除保存的认证状态
 */
export function clearAuthState(): void {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(AUTH_STATE_KEY);
    console.log('[AuthPersistence] 认证状态已清除');
  } catch (error) {
    console.warn('[AuthPersistence] 清除认证状态失败:', error);
  }
}

/**
 * 获取上次登录时间
 */
export function getLastLoginTime(): Date | null {
  try {
    const state = localStorage.getItem(AUTH_STATE_KEY);
    if (state) {
      const parsed = JSON.parse(state);
      return new Date(parsed.timestamp);
    }
  } catch (error) {
    console.warn('[AuthPersistence] 获取登录时间失败:', error);
  }
  return null;
}

/**
 * 开发环境专用：模拟登录（用于测试）
 */
export function mockLoginForDev(user: AuthUser): void {
  if (import.meta.env?.DEV) {
    saveAuthState(user);
    console.log('[AuthPersistence] 开发环境模拟登录:', user.email || user.id);
  }
}

/**
 * 开发环境专用：清除所有认证相关存储
 */
export function clearAllAuthStorage(): void {
  try {
    // 清除 localStorage 中的认证相关数据
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('foodbook:auth') || key.includes('tcb'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log('[AuthPersistence] 已清除所有认证存储:', keysToRemove.length, '项');
  } catch (error) {
    console.warn('[AuthPersistence] 清除认证存储失败:', error);
  }
}
